import { yupResolver } from '@hookform/resolvers/yup';
import { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';

import { VALIDATION_SCHEMAS } from '../../../constants';
import {
  getFormattedPhoneNumber,
  getPhoneNumbersToChange,
} from '../../../helpers';
import {
  BasicModal,
  Button,
  SelectController,
  TextInput,
  TextInputController,
} from '../../Shared';
import { PhoneNumbersField } from './PhoneNumbersField';

const EMPTY_STRING = '';

const getCountries = () => fetch('/countries');
const getStates = countryCode => fetch(`/states?countryCode=${countryCode}`);
const getCities = (countryCode, stateCode) =>
  fetch(
    `/cities?countryCode=${countryCode}` +
      (stateCode ? `&stateCode=${stateCode}` : ''),
  );

export const EditContactModal = ({
  open,
  onClose,
  contact,
  onChangeContact,
}) => {
  const [ianaTimeZone, setIanaTimeZone] = useState(contact.time ?? '');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [isLocationLoading, setIsLocationLoading] = useState(false);
  const [isLocationError, setIsLocationError] = useState(false);

  const hasCountries = countries.length > 0;
  const hasStates = states.length > 0;
  const hasCities = cities.length > 0;
  const isStateRequired = hasStates && !isLocationLoading;

  const defaultValues = {
    company: contact.company,
    contact: contact.contact ?? EMPTY_STRING,
    title: contact.title ?? EMPTY_STRING,
    industry: contact.industry ?? EMPTY_STRING,
    country: contact.country ?? EMPTY_STRING,
    state: contact.state ?? EMPTY_STRING,
    city: contact.city ?? EMPTY_STRING,
    address: contact.address ?? EMPTY_STRING,
    website: contact.website ?? EMPTY_STRING,
    email: contact.email ?? EMPTY_STRING,
    linkedin: contact.linkedin ?? EMPTY_STRING,
    phoneNumbers: contact.phoneNumbers.map(
      ({ PhoneNumber, IsBestPhoneNumber }) => ({
        value: getFormattedPhoneNumber(PhoneNumber),
        isBestPhoneNumber: Number(IsBestPhoneNumber),
      }),
    ),
  };

  const {
    control,
    formState: { isDirty, errors },
    setError,
    handleSubmit,
    setValue,
    getValues,
    trigger,
  } = useForm({
    resolver: yupResolver(VALIDATION_SCHEMAS.EDIT_CONTACT(isStateRequired)),
    mode: 'onChange',
    defaultValues,
  });

  const onSubmit = handleSubmit(async data => {
    setIsSubmitting(true);
    const { phoneNumbers: prevPhoneNumbers, ...restContactData } = contact;
    const { phoneNumbers, ...restFormData } = data;
    const {
      phoneNumbersToAdd,
      phoneNumbersToDelete,
      hasPhoneNumbersToAdd,
      hasPhoneNumbersToDelete,
    } = getPhoneNumbersToChange(prevPhoneNumbers, phoneNumbers);

    const body = {
      ...restContactData,
      ...restFormData,
      ...(hasPhoneNumbersToAdd && { phoneNumbersToAdd }),
      ...(hasPhoneNumbersToDelete && { phoneNumbersToDelete }),
    };

    try {
      const response = await fetch('/call-contact-update', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });

      if (response.status === 400) {
        const invalidFields = await response.json();
        invalidFields?.validationErrors?.forEach(invalidField => {
          Object.entries(invalidField).forEach(([field, errors]) => {
            setError(field, { message: errors[0] });
          });
        });
        setIsSubmitting(false);
        return;
      }

      if (response.status !== 200) throw new Error();

      const data = await response.json();

      onChangeContact({
        ...restContactData,
        ...restFormData,
        time: ianaTimeZone,
        phoneNumbers: data.record.PhoneNumbers,
      });
      setIsSubmitting(false);
      onClose();
    } catch {
      setIsSubmitting(false);
      toast.error("Can't save, please try again!", {
        position: 'top-right',
        autoClose: 3000,
      });
    }
  });

  const fetchCounties = useCallback(async () => {
    setIsLocationLoading(true);
    setIsLocationError(false);

    try {
      const countriesResponse = await getCountries();
      const countries = await countriesResponse.json();
      setCountries(countries);
      const country = countries.find(v => v.name === contact.country);

      if (!country) {
        setIsLocationLoading(false);
        return;
      }

      let stateCode = '';
      if (country.statesCount > 0) {
        const statesResponse = await getStates(country.isoCode);
        const states = await statesResponse.json();
        setStates(states);
        const state = states.find(v => v.name === contact.state);
        stateCode = state ? state.isoCode : '';
      }

      const citiesResponse = await getCities(country.isoCode, stateCode);
      const cities = await citiesResponse.json();
      setCities(cities);

      setIsLocationLoading(false);
    } catch {
      setIsLocationLoading(false);
      setIsLocationError(true);
      toast.error("Can't get Countries / States / Cities", {
        position: 'top-right',
        autoClose: 3000,
      });
    }
  }, [contact]);

  useEffect(() => {
    fetchCounties();
  }, [fetchCounties]);

  const updateStates = async countryName => {
    setIsLocationLoading(true);
    setValue('state', '');
    setValue('city', '');
    setStates([]);
    setCities([]);
    const country = countries.find(v => v.name === countryName);

    if (country.statesCount > 0) {
      const statesResponse = await getStates(country.isoCode);
      const states = await statesResponse.json();
      setStates(states);
    } else {
      const citiesResponse = await getCities(country.isoCode);
      const cities = await citiesResponse.json();
      setCities(cities);
    }
    setIsLocationLoading(false);
  };

  const updateCities = async stateName => {
    setIsLocationLoading(true);
    setValue('city', '');
    setCities([]);
    const countryName = getValues('country');
    const country = countries.find(v => v.name === countryName);
    const state = states.find(v => v.name === stateName);
    const citiesResponse = await getCities(country.isoCode, state.isoCode);
    const cities = await citiesResponse.json();
    setCities(cities);
    setIsLocationLoading(false);
  };

  const updateIanaTimeZone = cityName => {
    const city = cities.find(v => v.name === cityName);
    setIanaTimeZone(city.timezone);
  };

  const isSelectDisabled = isLocationLoading || isLocationError;
  const isSaveChangesButtonDisabled = isSubmitting || !isDirty;
  const isCountriesSelectDisabled = !hasCountries || isSelectDisabled;
  const isStatesSelectDisabled = !hasStates || isSelectDisabled;
  const isCitiesSelectDisabled = !hasCities || isSelectDisabled;

  return (
    <BasicModal open={open} onClose={isSubmitting ? undefined : onClose}>
      <div className="bg-white w-[576px] rounded-2xl shadow-[-40px_40px_80px_-8px_rgba(0,0,0,0.24)]">
        <div className="flex items-center justify-between py-4 pl-5 pr-4 border-b border-b-base-200">
          <span className="text-base-1000 font-semibold text-[22px]/[28px]">
            Contact Information
          </span>
          <button
            type="button"
            className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-transparent text-sm font-semibold text-gray-800 transition hover:bg-gray-100 disabled:pointer-events-none disabled:opacity-50 dark:text-white dark:hover:bg-gray-700 dark:focus:outline-none dark:focus:ring-1 dark:focus:ring-gray-600"
            data-hs-overlay="#edit-contact-modal"
            onClick={onClose}
            disabled={isSubmitting}
          >
            <span className="sr-only">Close</span>
            <svg
              className="h-4 w-4 flex-shrink-0"
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M18 6 6 18" />
              <path d="m6 6 12 12" />
            </svg>
          </button>
        </div>
        <form onSubmit={onSubmit}>
          <div className="overflow-y-auto p-5 max-h-[636px] h-full">
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <TextInputController
                  control={control}
                  name="company"
                  label="Company Name"
                  placeholder="Company Name"
                />
                <TextInputController
                  control={control}
                  name="contact"
                  label="Contact Name"
                  placeholder="Contact Name"
                />
              </div>
              <TextInputController
                control={control}
                name="title"
                label="Title"
                placeholder="Title"
              />
              <div className="grid grid-cols-2 gap-4">
                <TextInputController
                  control={control}
                  name="industry"
                  label="Industry"
                  placeholder="Industry"
                />
                <TextInput value={ianaTimeZone} readOnly />
              </div>
              <div className="grid grid-cols-3 gap-4">
                <SelectController
                  control={control}
                  name="country"
                  label="Country"
                  placeholder="Select Country"
                  options={countries.map((v, idx) => ({
                    id: idx,
                    label: v.name,
                    value: v.name,
                  }))}
                  disabled={isCountriesSelectDisabled}
                  handleChange={e => {
                    updateStates(e.target.value);
                  }}
                />
                <SelectController
                  control={control}
                  name="state"
                  label="State"
                  placeholder="Select State"
                  options={states.map((v, idx) => ({
                    id: idx,
                    label: v.name,
                    value: v.name,
                  }))}
                  disabled={isStatesSelectDisabled}
                  handleChange={e => {
                    updateCities(e.target.value);
                  }}
                />
                <SelectController
                  control={control}
                  name="city"
                  label="City"
                  placeholder="Select City"
                  options={cities.map((v, idx) => ({
                    id: idx,
                    label: v.name,
                    value: v.name,
                  }))}
                  disabled={isCitiesSelectDisabled}
                  handleChange={e => {
                    updateIanaTimeZone(e.target.value);
                  }}
                />
              </div>
              <PhoneNumbersField
                control={control}
                trigger={trigger}
                errors={errors}
              />
              <div className="grid grid-cols-1 gap-4">
                <TextInputController
                  control={control}
                  name="address"
                  label="Contact Address"
                  placeholder="Contact Address"
                />
                <TextInputController
                  control={control}
                  name="website"
                  label="Website"
                  placeholder="Website"
                />
                <TextInputController
                  control={control}
                  name="email"
                  label="Email"
                  placeholder="Email"
                />
                <TextInputController
                  control={control}
                  name="linkedin"
                  label="LinkedIn"
                  placeholder="LinkedIn"
                />
              </div>
            </div>
          </div>
          <div className="flex items-center justify-end gap-3 py-4 px-5 border-t border-t-base-200">
            <Button
              title="Cancel"
              variant="blue"
              onClick={onClose}
              disabled={isSubmitting}
            />
            <Button
              title="Save"
              type="submit"
              disabled={isSaveChangesButtonDisabled}
            />
          </div>
        </form>
      </div>
    </BasicModal>
  );
};
